import {
    CloseOutlined,
    DownloadOutlined,
    ExportOutlined,
    LockOutlined,
    RotateLeftOutlined,
    RotateRightOutlined,
    UndoOutlined,
    ZoomInOutlined,
    ZoomOutOutlined,
} from "@ant-design/icons";
import { Button, Checkbox, Segmented, Space, Tooltip, Typography } from "antd";
import mergeClassNames from "common/mergeClassNames";
import { VIEWER_MODE_LABELS } from "./utils";

const { Text } = Typography;

/**
 * The control bar shared by every viewing mode so the UI stays consistent
 * across overlay, modal, side panel and floating window. Colours adapt to the
 * surrounding surface via `tone` ("dark" for the overlay, "light" otherwise).
 */
const AttachmentToolbar = ({
    file,
    index,
    total,
    isImage,
    tone = "dark",
    onZoomIn,
    onZoomOut,
    onRotateLeft,
    onRotateRight,
    onReset,
    onDownload,
    onOpenNewTab,
    onClose,
    mode,
    onModeChange,
    availableModes = [],
    remember,
    onToggleRemember,
    piiProtected = false,
    secondsRemaining,
}) => {
    const isDark = tone === "dark";
    const btnClass = isDark ? "border-none bg-transparent text-white hover:!bg-white/20 hover:!text-white" : "";
    const titleText = isDark ? "text-white" : "text-gray-800";
    const subText = isDark ? "text-gray-300" : "text-gray-500";

    const iconBtn = (key, title, icon, onClick) => (
        <Tooltip title={title} key={key}>
            <Button aria-label={title} icon={icon} size="small" onClick={onClick} className={btnClass} />
        </Tooltip>
    );

    return (
        <div className={mergeClassNames("flex flex-wrap items-center gap-x-4 gap-y-2", isDark ? "" : "border-b border-gray-100 pb-2")}>
            {/* Title + counter */}
            <div className="flex min-w-0 flex-1 items-center gap-2">
                <Text className={mergeClassNames("truncate text-sm font-medium", titleText)} title={file?.name}>
                    {file?.name || "Attachment"}
                </Text>
                {total > 1 && (
                    <Text className={mergeClassNames("whitespace-nowrap text-xs", subText)}>
                        ({index + 1} of {total})
                    </Text>
                )}
            </div>

            {/* PII auto-close indicator */}
            {piiProtected && (
                <Tooltip title="For your security this view closes automatically after inactivity">
                    <span className={mergeClassNames("flex items-center gap-1 whitespace-nowrap text-xs", subText)}>
                        <LockOutlined />
                        Auto-close in {Math.max(0, secondsRemaining ?? 0)}s
                    </span>
                </Tooltip>
            )}

            {/* Image-specific controls */}
            {isImage && (
                <Space size={4}>
                    {iconBtn("zoom-out", "Zoom out", <ZoomOutOutlined />, onZoomOut)}
                    {iconBtn("zoom-in", "Zoom in", <ZoomInOutlined />, onZoomIn)}
                    {iconBtn("rotate-left", "Rotate left", <RotateLeftOutlined />, onRotateLeft)}
                    {iconBtn("rotate-right", "Rotate right", <RotateRightOutlined />, onRotateRight)}
                    {iconBtn("reset", "Reset", <UndoOutlined />, onReset)}
                </Space>
            )}

            {/* Common actions */}
            <Space size={4}>
                {iconBtn("download", "Download", <DownloadOutlined />, onDownload)}
                {iconBtn("open-tab", "Open in new tab", <ExportOutlined />, onOpenNewTab)}
            </Space>

            {/* Mode switcher + remember preference */}
            {availableModes.length > 1 && (
                <div className="flex items-center gap-2">
                    <Segmented
                        size="small"
                        value={mode}
                        onChange={onModeChange}
                        options={availableModes.map((m) => ({ label: VIEWER_MODE_LABELS[m] || m, value: m }))}
                    />
                    {onToggleRemember && (
                        <Tooltip title="Use this layout by default next time">
                            <Checkbox
                                checked={remember}
                                onChange={(e) => onToggleRemember(e.target.checked)}
                                className={mergeClassNames("whitespace-nowrap text-xs", subText)}
                            >
                                Remember
                            </Checkbox>
                        </Tooltip>
                    )}
                </div>
            )}

            {/* Close */}
            {onClose && iconBtn("close", "Close", <CloseOutlined />, onClose)}
        </div>
    );
};

export default AttachmentToolbar;
